import { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useAuthStore } from 'src/app/auth/store/auth-store'
import { Toast, toastTypes } from 'src/app/core/services/toast'
import { useListUrlState } from 'src/app/home/hooks/use-list-url-state'
import { useFundUpdateCategoriesQuery } from 'src/app/home/repositories/use-fund-update-categories-query'
import { useFundUpdateDownloadQuery } from 'src/app/home/repositories/use-fund-update-download-query'
import { useFundUpdatesQuery } from 'src/app/home/repositories/use-fund-updates-query'
import { useFundUpdatesStatsQuery } from 'src/app/home/repositories/use-fund-updates-stats-query'

import type {
  DocumentUpdateCategoryFilter,
  IDocumentUpdateEntity,
  IUseFundUpdatesReturn,
} from '../types'

const PAGE_SIZE = 6

export const useFundUpdates = (): IUseFundUpdatesReturn => {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())

  const {
    page: currentPage,
    filters,
    setPage: setCurrentPage,
    setFilter,
  } = useListUrlState({
    filters: [{ key: 'category', default: 'all' }],
  })

  const categoryFilter = filters.category as DocumentUpdateCategoryFilter
  const handleSetCategoryFilter = useCallback(
    (value: DocumentUpdateCategoryFilter): void => setFilter('category', value),
    [setFilter],
  )

  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentUpdateEntity | null>(null)
  const [selectedDocumentBlobUrl, setSelectedDocumentBlobUrl] = useState<
    string | null
  >(null)

  const downloadQuery = useFundUpdateDownloadQuery({
    documentUrl: selectedDocument?.documentUrl ?? '',
    enabled: !!selectedDocument,
  })

  useEffect((): (() => void) | void => {
    if (!downloadQuery.data || !selectedDocument?.id) {
      return
    }
    const url = URL.createObjectURL(downloadQuery.data)
    setSelectedDocumentBlobUrl(url)
    return (): void => {
      URL.revokeObjectURL(url)
    }
  }, [downloadQuery.data, selectedDocument?.id])

  const apiCategory = categoryFilter === 'all' ? '' : categoryFilter

  const listQuery = useFundUpdatesQuery({
    page: currentPage,
    pageSize: PAGE_SIZE,
    category: apiCategory,
    enabled: isAuthenticated,
    silentErrorToast: true,
  })

  const statsQuery = useFundUpdatesStatsQuery({
    enabled: isAuthenticated,
    silentErrorToast: true,
  })

  const categoriesQuery = useFundUpdateCategoriesQuery({
    enabled: isAuthenticated,
  })

  useEffect((): void => {
    const hasError = listQuery.isError || statsQuery.isError
    if (!hasError) {
      return
    }

    Toast.notify({
      type: toastTypes.ERROR,
      title: t('fundUpdates.loadErrorToastTitle'),
      message: t('fundUpdates.loadErrorToastMessage'),
      options: { toastId: 'fund-updates-fetch-error' },
    })
  }, [listQuery.isError, statsQuery.isError, t])

  useEffect((): void => {
    if (!downloadQuery.isError) {
      return
    }

    Toast.notify({
      type: toastTypes.ERROR,
      title: 'Failed to download document',
      message: 'This document could not be downloaded. Please try again.',
      options: { toastId: 'fund-update-download-error' },
    })
  }, [downloadQuery.isError])

  const closeDocumentPreview = useCallback((): void => {
    setSelectedDocument(null)
    setSelectedDocumentBlobUrl(null)
  }, [])

  const openDocumentPreview = useCallback(
    (document: IDocumentUpdateEntity): void => {
      setSelectedDocumentBlobUrl(null)
      setSelectedDocument(document)
    },
    [],
  )

  const pageItems = useMemo((): IDocumentUpdateEntity[] => {
    return listQuery.data?.items ?? []
  }, [listQuery.data?.items])

  const total = listQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const isLoading = listQuery.isLoading || statsQuery.isLoading
  const isError = listQuery.isError || statsQuery.isError
  const isEmpty =
    listQuery.isSuccess && (listQuery.data?.items?.length ?? 0) === 0

  const summaryAsOf = statsQuery.data?.summaryAsOf ?? ''
  const fundSummaryItems = statsQuery.data?.fundSummaryItems ?? []

  const categoryOptions = useMemo<string[]>(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  )

  return {
    isLoading,
    isError,
    isEmpty,
    categoryFilter,
    categoryOptions,
    isCategoriesLoading: categoriesQuery.isLoading,
    isCategoriesError: categoriesQuery.isError,
    currentPage,
    totalPages,
    pageItems,
    summaryAsOf,
    fundSummaryItems,
    selectedDocument,
    selectedDocumentBlobUrl,
    setCategoryFilter: handleSetCategoryFilter,
    setCurrentPage,
    openDocumentPreview,
    closeDocumentPreview,
  }
}
